import { i18n } from '/locales/i18n'

type CloseResolveValue = 'close' | 'false'

export type DialogOptions = {
  title?: string
  imageUrl?: string
  okLabel?: string
  doNotResolveOnClose?: boolean
}

export default class Dialog {
  protected message: string | DocumentFragment
  protected title?: string
  protected imageUrl?: string
  protected okLabel: string
  protected closeResolveValue: CloseResolveValue
  protected element!: HTMLElement

  /** Creates a dialog ready to be rendered with `show()`. */
  constructor(message: string | DocumentFragment, options: DialogOptions = {}) {
    this.message = message
    this.title = options.title
    this.imageUrl = options.imageUrl
    this.okLabel = options.okLabel || i18n.t('js.common.ok')
    this.closeResolveValue = options.doNotResolveOnClose ? 'close' : 'false'
  }

  /** Inserts the dialog in the DOM and resolves with the selected action. */
  show(): Promise<boolean> {
    this.buildNode()

    document.body.appendChild(this.element)

    return new Promise((resolve) => {
      this.element
        .querySelectorAll<HTMLElement>('[data-dialog-resolve]')
        .forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              const target = event.currentTarget as HTMLElement
              this.remove()
              if (target.dataset.dialogResolve === 'close') return

              resolve(target.dataset.dialogResolve === 'true')
            },
            { once: true },
          )
        })
    })
  }

  /** Removes the dialog from the DOM. */
  remove() {
    this.element.remove()
  }

  protected templateContent() {
    return `
      ${this.templateImage()}
      ${this.templateTitle()}
      <div data-dialog-message></div>
      <modal-footer>
        <button data-dialog-resolve="true" class="button is-secondary">${this.okLabel}</button>
      </modal-footer>
    `
  }

  protected templateTitle() {
    return `<h1>${this.title || '&nbsp;'}</h1>`
    // "nbsp" leaves enough space for long text when there is no title.
  }

  protected templateImage() {
    return this.imageUrl
      ? `<img src="${this.imageUrl}" width="154" height="154" class="mx-auto" alt="">`
      : ''
  }

  private buildNode() {
    const placeholder = document.createElement('div')
    placeholder.innerHTML = this.template()

    const messageNode = placeholder.querySelector<HTMLElement>(
      '[data-dialog-message]',
    )
    if (!messageNode) throw new Error('Dialog message placeholder is missing')

    if (this.message instanceof DocumentFragment)
      messageNode.replaceWith(this.message)
    else messageNode.innerHTML = this.message

    const element = placeholder.firstElementChild
    if (!(element instanceof HTMLElement))
      throw new Error('Dialog template must render a root element')

    this.element = element
  }

  private template() {
    return `
      <modal-slot>
        <modal-dialog>
          <button data-dialog-resolve="${this.closeResolveValue}" class="small-icon-button close-modal-button">
            <font-icon class="cross"></font-icon>
          </button>
          <modal-body data-modal="content">
            ${this.templateContent()}
          </modal-body>
        </modal-dialog>
        <modal-overlay data-dialog-resolve="${this.closeResolveValue}"></modal-overlay>
      </modal-slot>
    `
  }
}
